import { useState, useEffect } from "react";

type StoredValue<T> = {
  value: T;
  expiry?: number;
};

export function useLocalStorage<T>(
  key: string,
  defaultValue: T,
  options?: { ttl?: number } // TTL = Time to live in ms
): [T, (val: T) => void] {
  const { ttl } = options || {};

  const readValue = (): T => {
    if (typeof localStorage === "undefined") {
      return defaultValue;
    }
    try {
      const item = localStorage.getItem(key);
      if (!item) return defaultValue;

      const parsed: StoredValue<T> = JSON.parse(item);

      if (parsed.expiry && Date.now() > parsed.expiry) {
        localStorage.removeItem(key);
        return defaultValue;
      }

      return parsed.value ?? defaultValue;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      try {
        if (typeof localStorage !== "undefined") {
          localStorage.removeItem(key);
        }
      } catch {}
      return defaultValue;
    }
  };

  const [storedValue, setStoredValue] = useState<T>(readValue);

  useEffect(() => {
    if (typeof localStorage === "undefined") {
      return;
    }
    try {
      const payload: StoredValue<T> = {
        value: storedValue,
        ...(ttl ? { expiry: Date.now() + ttl } : {}),
      };
      localStorage.setItem(key, JSON.stringify(payload));
    } catch (error) {
      console.warn(`Error writing to localStorage key "${key}":`, error);
    }
  }, [key, storedValue, ttl]);

  return [storedValue, setStoredValue];
}
